import { Component, OnChanges, SimpleChanges, ChangeDetectionStrategy, input, output } from '@angular/core';
import {UnitConversionUtils} from '../../configurator/unit-conversion-utils';
import {CoilRequest} from '../../model/coil-request';
import {StringUtils} from '../../utils/string-utils';

import {FormsModule} from '@angular/forms';

@Component({
    selector: 'app-schedule-line-performance-coil-input',
    templateUrl: './schedule-line-performance-coil-input.component.html',
    styleUrls: ['./schedule-line-performance-coil-input.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule]
})
export class ScheduleLinePerformanceCoilInputComponent implements OnChanges {

  readonly fieldLabel = input.required<string>();
  readonly coilRequestInput = input.required<CoilRequest>({ alias: 'coilRequest' });
  readonly metricInput = input.required<boolean>();
  readonly inputChanged = output<void>();
  readonly metricInputUpdated = output<void>();
  deltaType: string = 'tempChange';
  deltaValue!: number;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['coilRequestInput'] && changes['coilRequestInput'].firstChange) {
      if (StringUtils.isEmptyNumericValue(this.coilRequestInput().TemperatureChange)) {
        this.deltaValue = this.coilRequestInput().FlowRate;
        this.deltaType = 'flowRate';
      } else {
        this.deltaValue = this.coilRequestInput().TemperatureChange;
        this.deltaType = 'tempChange';
      }
    }
    if (changes['metricInput'] && !changes['metricInput'].firstChange) {
      this.setDeltaValues();
      this.convertConditionalValues();
      this.metricInputUpdated.emit();
    }
  }

  private convertConditionalValues(): void {
    this.coilRequestInput().EnteringDryBulb = this.convertAndRoundTemperature(this.coilRequestInput().EnteringDryBulb);
    if (this.coilRequestInput().isFluid) {
      this.coilRequestInput().EnteringFluidTemperature = this.convertAndRoundTemperature(this.coilRequestInput().EnteringFluidTemperature);
    }
    if (this.coilRequestInput().isCooling) {
      this.coilRequestInput().EnteringWetBulb = this.convertAndRoundTemperature(this.coilRequestInput().EnteringWetBulb);
    }
    if (this.coilRequestInput().isRefrigerant) {
      this.coilRequestInput().SaturationSuctionTemperature = this.convertAndRoundTemperature(this.coilRequestInput().SaturationSuctionTemperature);
    }
    if (this.coilRequestInput().isSteam) {
      this.coilRequestInput().EnteringSteamPressure = this.round(UnitConversionUtils.convertSteamPressure(this.coilRequestInput().EnteringSteamPressure, this.metricInput()), 3);
    }
  }

  private convertAndRoundTemperature(input: number): number {
    if (StringUtils.isEmptyNumericValue(input)) {
      return undefined!;
    }
    return this.round(UnitConversionUtils.convertTemperatureValue(input, this.metricInput()), 3);
  }

  private round(value: string, digits: number): number {
    const numericValue = +value;
    const roundedValue = numericValue.toFixed(digits);
    return +roundedValue;
  }

  inputValueChange(): void {
    this.inputChanged.emit();
  }

  getTemperatureUnits(): string {
    return UnitConversionUtils.getTemperatureUnits(this.metricInput());
  }

  getFlowRateUnits(): string {
    return UnitConversionUtils.getFlowRateUnits(this.metricInput());
  }

  deltaTypeChanged(): void {
    this.coilRequestInput().TemperatureChange = undefined!;
    this.coilRequestInput().FlowRate = undefined!;
    this.setDeltaValues();
    this.inputValueChange();
  }

  deltaValueChanged(): void {
    if ('tempChange' === this.deltaType) {
      this.coilRequestInput().TemperatureChange = this.deltaValue;
    } else {
      this.coilRequestInput().FlowRate = this.deltaValue;
    }
    this.inputValueChange();
  }

  private setDeltaValues(): void {
    if ('tempChange' === this.deltaType) {
      if (this.coilRequestInput().EnteringWetBulb) {
        this.coilRequestInput().TemperatureChange = this.metricInput() ? 5.6 : 10;
      } else {
        this.coilRequestInput().TemperatureChange = this.metricInput() ? 10 : 20;
      }
      this.deltaValue = this.coilRequestInput().TemperatureChange;
      this.coilRequestInput().FlowRate = undefined!;
    } else {
      this.coilRequestInput().FlowRate = this.metricInput() ? 0.2 : 3;
      this.deltaValue = this.coilRequestInput().FlowRate;
      this.coilRequestInput().TemperatureChange = undefined!;
    }
  }

  getDeltaUnits(): string {
    return 'tempChange' === this.deltaType ? this.getTemperatureUnits() : this.getFlowRateUnits();
  }

  getSteamPressureUnits(): string {
    return UnitConversionUtils.getSteamPressureUnits(this.metricInput());
  }

  fluidTypeChanged(): void {
    if ('W' === this.coilRequestInput().FluidType) {
      this.coilRequestInput().GlycolPercentage = undefined!;
    } else {
      this.coilRequestInput().GlycolPercentage = 30;
    }
    this.inputValueChange();
  }

  showGlycolPercent(): boolean {
    return StringUtils.equalsIgnoreCase(this.coilRequestInput().FluidType, 'E') || StringUtils.equalsIgnoreCase(this.coilRequestInput().FluidType, 'P');
  }

}
